import classnames from 'classnames';
import { useTranslation } from 'react-i18next';

import style from './DurationForm.module.scss';

import type { DurationUpdateParams } from './hooks/types';
import type { Nullable } from 'chaika-common-types';

import { DatePicker } from '@components/datePickers';
import { OptionType, Select } from '@components/Select';
import { DurationFormDataTestIds, SchedulePageAttributes } from 'chaika-test-attributes';
import { translate } from '@chaika/i18n/helper';

import { useDurationForm } from './hooks/useDurationForm';
import { DateHelper, DateTypes } from '@utils/DateHelper';
import { TimeInputSelect } from '@components/TimeSelect';
import { observer } from 'mobx-react';

export interface DurationFormProps {
    name?: string;
    withTime?: boolean;
    duration?: Nullable<number>;
    endDate?: Date | null;
    disableInfinityDuration?: boolean;
    isStartDateClearable?: boolean;
    isClearable?: boolean;
    disableEdit?: boolean;
    maxDate?: Date;
    minDate?: Date;
    maxStartDate?: Date;
    withoutEndDateSelect?: boolean;
    withoutDurationSelect?: boolean;
    fullWidth?: boolean;
    startDate?: Date | null;
    startTitle?: string;
    endTitle?: string;
    disableEditStartTime?: boolean;
    durationOptions?: OptionType[];
    onDateChange(params: DurationUpdateParams, duration?: number | null, name?: string): void;
}

export enum TimeSelectName {
    Start = 'start',
    End = 'end',
}

export const DurationForm = observer(
    ({
        name,
        startDate,
        maxDate,
        minDate,
        maxStartDate,
        disableInfinityDuration,
        onDateChange,
        withoutEndDateSelect = false,
        withoutDurationSelect = false,
        fullWidth = false,
        isStartDateClearable = false,
        isClearable = false,
        startTitle = translate('start'),
        endTitle = translate('end'),
        disableEditStartTime,
        disableEdit,
        withTime,
        ...props
    }: DurationFormProps) => {
        const {
            duration,
            endDate,
            durationOptions,
            onDurationChange,
            onStartDateChange,
            onEndDateChange,
            onTimeChange,
        } = useDurationForm({ name, startDate, disableInfinityDuration, onDateChange, ...props });
        const { i18n } = useTranslation();

        return (
            <div
                data-test={DurationFormDataTestIds.Root}
                data-testid={DurationFormDataTestIds.Root}
                className={classnames([style.root, fullWidth && style.root_fullWidth])}
            >
                <div className={style.datepicker}>
                    <DatePicker
                        locale={i18n.language}
                        dataTest={DurationFormDataTestIds.StartDateInput}
                        maxDate={maxStartDate ?? maxDate}
                        minDate={minDate}
                        isClearable={isStartDateClearable}
                        disabled={disableEditStartTime || disableEdit}
                        title={startTitle}
                        name={DateTypes.START}
                        selected={startDate ?? null}
                        onChange={onStartDateChange}
                    />
                    {withTime && (
                        <TimeInputSelect
                            name={TimeSelectName.Start}
                            dataTest={SchedulePageAttributes.ReservationFormStartTime}
                            disabled={disableEdit}
                            time={DateHelper.makeTimeData(startDate ?? new Date())}
                            onSelect={onTimeChange}
                        />
                    )}
                </div>
                {!withoutEndDateSelect && (
                    <div
                        data-test={DurationFormDataTestIds.EndDateWrapper}
                        data-testid={DurationFormDataTestIds.EndDateWrapper}
                        className={style.datepicker}
                    >
                        <DatePicker
                            locale={i18n.language}
                            dataTest={DurationFormDataTestIds.EndDateInput}
                            disabled={disableEdit}
                            maxDate={maxDate}
                            isClearable={isClearable}
                            title={endTitle}
                            openToDate={!!endDate ? undefined : (startDate ?? undefined)}
                            name={DateTypes.END}
                            minDate={startDate}
                            selected={endDate ?? null}
                            onChange={onEndDateChange}
                        />
                        {withTime && (
                            <TimeInputSelect
                                name={TimeSelectName.End}
                                dataTest={SchedulePageAttributes.ReservationFormStartTime}
                                disabled={disableEdit}
                                time={DateHelper.makeTimeData(endDate ?? new Date())}
                                onSelect={onTimeChange}
                            />
                        )}
                    </div>
                )}
                {!withoutDurationSelect && (
                    <div
                        className={style.duration}
                        data-test={DurationFormDataTestIds.DurationSelectWrapper}
                        data-testid={DurationFormDataTestIds.DurationSelectWrapper}
                    >
                        <Select
                            dataTest={DurationFormDataTestIds.DurationSelect}
                            disabled={disableEdit}
                            selectedValue={duration}
                            options={durationOptions}
                            onChange={onDurationChange}
                        />
                    </div>
                )}
            </div>
        );
    },
);
